"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState, useRef } from "react";
import { useInView, useSpring, useTransform } from "motion/react";
import { cva, VariantProps } from "class-variance-authority";
import { ImageProps } from "./Image";
import { containsNonNumeric } from "@/lib/utils";
export interface FactItemProps extends VariantProps<typeof factItemVariants> {
  id?: string;
  value: number | string;
  subline: string;
  unit?: string;
  showPlus?: boolean;
  useCounter?: boolean;
  className?: string;
  image?: ImageProps;
}
const factItemVariants = cva("w-full flex flex-col items-center text-center", {
  variants: {
    variant: {
      light: "text-white",
      dark: "text-black"
    }
  },
  defaultVariants: {
    variant: "light"
  }
});

/**
 * FactItem component that displays a numeric value with a subline.
 * Supports count-up animation and optional plus sign.
 */
export const FactItem: React.FC<FactItemProps> = t0 => {
  const $ = _c(39);
  const {
    id,
    value,
    subline,
    unit: t1,
    showPlus: t2,
    useCounter: t3,
    variant: t4,
    className: t5
  } = t0;
  const unit = t1 === undefined ? "" : t1;
  const showPlus = t2 === undefined ? false : t2;
  const useCounter = t3 === undefined ? true : t3;
  const variant = t4 === undefined ? "light" : t4;
  const className = t5 === undefined ? "" : t5;
  const ref = useRef(null);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      once: true,
      amount: 0.5
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const isInView = useInView(ref, t6);
  const t7 = String(value);
  let t8;
  if ($[1] !== t7) {
    t8 = containsNonNumeric(t7);
    $[1] = t7;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const isNumber = !t8;
  const isAnimatedCounter = useCounter && isNumber;
  const [displayValue, setDisplayValue] = useState(isAnimatedCounter ? 0 : value);
  let t9;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 2000,
      bounce: 0
    };
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const springValue = useSpring(0, t9);
  const rounded = useTransform(springValue, _temp);
  let t10;
  let t11;
  if ($[4] !== isAnimatedCounter || $[5] !== isInView || $[6] !== springValue || $[7] !== value) {
    t10 = () => {
      if (isInView && isAnimatedCounter) {
        springValue.set(value as number);
      }
    };
    t11 = [isInView, value, isAnimatedCounter, springValue];
    $[4] = isAnimatedCounter;
    $[5] = isInView;
    $[6] = springValue;
    $[7] = value;
    $[8] = t10;
    $[9] = t11;
  } else {
    t10 = $[8];
    t11 = $[9];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[10] !== isAnimatedCounter || $[11] !== rounded) {
    t12 = () => {
      if (isAnimatedCounter) {
        return rounded.on("change", latest_0 => {
          setDisplayValue(latest_0);
        });
      }
    };
    t13 = [rounded, isAnimatedCounter];
    $[10] = isAnimatedCounter;
    $[11] = rounded;
    $[12] = t12;
    $[13] = t13;
  } else {
    t12 = $[12];
    t13 = $[13];
  }
  useEffect(t12, t13);
  const t14 = "fact-" + (id ?? unit);
  let t15;
  if ($[14] !== className || $[15] !== variant) {
    t15 = factItemVariants({
      variant,
      className
    });
    $[14] = className;
    $[15] = variant;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  if ($[17] !== value) {
    t16 = <span className="invisible select-none" aria-hidden="true">{value}</span>;
    $[17] = value;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== displayValue) {
    t17 = <span className="absolute left-0">{displayValue}</span>;
    $[19] = displayValue;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== t16 || $[22] !== t17) {
    t18 = <div className="relative flex items-baseline">{t16}{t17}</div>;
    $[21] = t16;
    $[22] = t17;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== unit) {
    t19 = unit && <span className="ml-sm">{unit}</span>;
    $[24] = unit;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== showPlus) {
    t20 = showPlus && <span>+</span>;
    $[26] = showPlus;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t18 || $[29] !== t19 || $[30] !== t20) {
    t21 = <div className="headline-1 flex items-baseline font-medium text-5xl">{t18}{t19}{t20}</div>;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== subline) {
    t22 = <div className="text-md mt-xs tracking-wider font-regular text-3xl">{subline}</div>;
    $[32] = subline;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t14 || $[35] !== t15 || $[36] !== t21 || $[37] !== t22) {
    t23 = <div data-id={t14} ref={ref} className={t15}>{t21}{t22}</div>;
    $[34] = t14;
    $[35] = t15;
    $[36] = t21;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  return t23;
};
function _temp(latest) {
  return Math.round(latest);
}